import { Box, Typography, Chip } from '@mui/material';
import React from 'react';
import { useLocales } from '../../../hooks';
import { MenuItemUiModuleBody, MenuSectionUiModuleBody } from '../../../API';

type MenuItem = MenuItemUiModuleBody;

interface MenuItemsComparisonProps {
  oldValue: string | null | undefined | object;
  newValue: string | null | undefined | object;
}

export function MenuItemsComparison({ oldValue, newValue }: MenuItemsComparisonProps): React.ReactElement {
  const { t } = useLocales();

  return (
    <Box display="flex" gap={2}>
      {/* Before (Source) */}
      <Box flex={1} display="flex" flexDirection="column" gap={1}>
        <Typography variant="caption" color="white" fontWeight="bold">
          {t('layouts.before_source')}:
        </Typography>
        {renderMenuItems(oldValue, 'source', oldValue, newValue)}
      </Box>
      {/* After (Target) */}
      <Box flex={1} display="flex" flexDirection="column" gap={1}>
        <Typography variant="caption" color="white" fontWeight="bold">
          {t('layouts.after_target')}:
        </Typography>
        {renderMenuItems(newValue, 'target', oldValue, newValue)}
      </Box>
    </Box>
  );
}

const renderMenuItems = (
  value: string | null | undefined | object,
  type: 'source' | 'target',
  oldValue: string | null | undefined | object,
  newValue: string | null | undefined | object
): React.ReactElement => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return (
      <Typography variant="caption" color="white">
        none
      </Typography>
    );
  }

  try {
    // If it's a string, try to parse it as JSON
    let menuItems: MenuItem[] = [];
    if (typeof value === 'string') {
      // Try to extract menu items from the comma-separated string
      const items = value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item);
      if (items.length > 0) {
        return (
          <Box display="flex" flexDirection="column" gap={1}>
            {items.slice(0, 5).map((item, index) => (
              <Typography key={index} variant="caption" color="white">
                • {item}
              </Typography>
            ))}
            {items.length > 5 && (
              <Typography variant="caption" color="white">
                ... and {items.length - 5} more
              </Typography>
            )}
          </Box>
        );
      }
    } else if (Array.isArray(value)) {
      menuItems = value;
    } else if (typeof value === 'object') {
      // If it's an object, try to extract menu items
      const obj = value as MenuSectionUiModuleBody;
      if (obj.menuItems) {
        menuItems = obj.menuItems;
      } else if (Array.isArray(obj)) {
        menuItems = obj;
      }
    }

    if (menuItems.length > 0) {
      return (
        <Box display="flex" flexDirection="column" gap={1}>
          {menuItems.slice(0, 5).map((item, index) => {
            const title = item.title || item.titleLokaliseKey || 'Untitled';
            const targetId = item.targetId || '';
            const platforms = item.targetPlatform || [];
            const titleText = typeof title === 'object' ? Object.values(title)[0] || 'Untitled' : title;

            // Check if this item has changed
            const isModified = isItemModified(item, oldValue, newValue, type);

            return (
              <Box key={index} display="flex" flexDirection="column" gap={1}>
                <Typography variant="caption" color={isModified ? '#FFD700' : 'white'} fontWeight="bold">
                  • {titleText}
                </Typography>
                {targetId && (
                  <Typography variant="caption" color={isModified ? '#FFD700' : 'white'} sx={{ ml: 1 }}>
                    → {targetId}
                  </Typography>
                )}
                {platforms.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={1} sx={{ ml: 1 }}>
                    {platforms.slice(0, 3).map((platform: string, pIndex: number) => (
                      <Chip
                        key={pIndex}
                        label={platform}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: isModified ? '#FFD700' : 'white',
                          borderColor: isModified ? '#FFD700' : 'white',
                          '& .MuiChip-label': { color: isModified ? '#FFD700' : 'white' }
                        }}
                      />
                    ))}
                    {platforms.length > 3 && (
                      <Chip
                        label={`+${platforms.length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: isModified ? '#FFD700' : 'white',
                          borderColor: isModified ? '#FFD700' : 'white',
                          '& .MuiChip-label': { color: isModified ? '#FFD700' : 'white' }
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
          {menuItems.length > 5 && (
            <Typography variant="caption" color="white">
              ... and {menuItems.length - 5} more items
            </Typography>
          )}
        </Box>
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error parsing menu items:', error);
  }

  return (
    <Typography variant="caption" color="white">
      {String(value)}
    </Typography>
  );
};

const isItemModified = (
  item: MenuItem,
  oldValue: string | null | undefined | object,
  newValue: string | null | undefined | object,
  type: 'source' | 'target'
): boolean => {
  try {
    // Extract menu items from both old and new values
    const oldItems = extractMenuItems(oldValue);
    const newItems = extractMenuItems(newValue);

    // Find the corresponding item in the other array
    const itemTitle = typeof item.title === 'object' ? Object.values(item.title)[0] : item.title;
    const itemTargetId = item.targetId;

    const otherItems = type === 'source' ? newItems : oldItems;
    const correspondingItem = otherItems.find((otherItem) => {
      const otherTitle = typeof otherItem.title === 'object' ? Object.values(otherItem.title)[0] : otherItem.title;
      return otherTitle === itemTitle && otherItem.targetId === itemTargetId;
    });

    if (!correspondingItem) {
      return true; // Item was added or removed
    }

    // Compare properties
    return (
      JSON.stringify(item.targetPlatform?.sort()) !== JSON.stringify(correspondingItem.targetPlatform?.sort()) ||
      item.targetId !== correspondingItem.targetId ||
      JSON.stringify(item.title) !== JSON.stringify(correspondingItem.title)
    );
  } catch (error) {
    return false;
  }
};

const extractMenuItems = (value: string | null | undefined | object): MenuItem[] => {
  if (!value) return [];

  try {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'object') {
      const obj = value as MenuSectionUiModuleBody;
      if (obj.menuItems) {
        return obj.menuItems;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error extracting menu items:', error);
  }

  return [];
};
